/**
 * Product Source of Truth v1 : shared vocabulary and catalog invariants.
 * No recommendation scoring, compatibility rules, or builder logic.
 *
 * Architecture locks (do not invert later):
 * 1. LiveScope 2 (LVS44, LVS42HD, LVS42HD-IF) satisfies LIVE_SONAR and
 *    SONAR_PROCESSOR_INTERFACE. Do not model this as DOES_NOT_REQUIRE_GLS10.
 *    Plus-generation transducers (LVS32, LVS34, LVS34-IF, LVS62) satisfy
 *    LIVE_SONAR only; GLS 10 is the SONAR_PROCESSOR_INTERFACE.
 * 2. Approved batteries satisfy POWER_SOURCE only. Do not put batteries in a
 *    product compatibility graph. Runtime/current-draw belongs to a later power model.
 */

const ENTITY_KINDS = Object.freeze(['product', 'package']);

const RECOMMENDATION_STATUSES = Object.freeze([
  'RECOMMENDED',
  'SUPPORTED',
  'OWNERSHIP_ONLY',
  'DO_NOT_RECOMMEND',
]);

const LIFECYCLE_STATUSES = Object.freeze([
  'CURRENT',
  'LEGACY',
  'DISCONTINUED',
]);

const SYSTEM_ROLES = Object.freeze([
  'LIVE_SONAR',
  'DISPLAY',
  'SONAR_PROCESSOR_INTERFACE',
  'POWER_SOURCE',
  'PORTABLE_PLATFORM',
  'DISPLAY_MOUNTING',
  'TRANSDUCER_POSITIONING',
  'TRANSDUCER_ADAPTER',
  'CONNECTIVITY',
  'CHARGER',
  'PROTECTION',
]);

const OFFER_CONDITIONS = Object.freeze([
  'new',
  'refurbished',
  'used',
  'unknown',
]);

/**
 * @typedef {Object} HardwareFamilyRecord
 * @property {string} slug
 * @property {string} name
 * @property {string} [brand]
 * @property {string} [notes]
 */

/**
 * @typedef {Object} CanonicalProductRecord
 * @property {string} slug
 * @property {'product'|'package'} entityKind
 * @property {string} name
 * @property {string} [brand]
 * @property {string} [category]
 * @property {string} [modelNumber]
 * @property {string} [mpn]
 * @property {number|null} [msrp]
 * @property {string} [imageUrl]
 * @property {string} [hardwareFamilySlug]
 * @property {'RECOMMENDED'|'SUPPORTED'|'OWNERSHIP_ONLY'|'DO_NOT_RECOMMEND'} recommendationStatus
 * @property {'CURRENT'|'LEGACY'|'DISCONTINUED'} lifecycleStatus
 * @property {string} [intendedUseLabel]
 * @property {string} [localCatalogId]
 * @property {string[]} [roles]
 * @property {string[]} [aliases]
 * @property {string} [notes]
 * @property {boolean} [createIfMissing]
 */

/**
 * @typedef {Object} PackageContentRecord
 * @property {string} packageSlug
 * @property {string} componentSlug
 * @property {number} [quantity]
 * @property {boolean} [isRequired]
 * @property {string} [inclusionNotes]
 * @property {number} [sortOrder]
 */

/**
 * @typedef {Object} CanonicalUniverse
 * @property {HardwareFamilyRecord[]} hardwareFamilies
 * @property {CanonicalProductRecord[]} products
 * @property {PackageContentRecord[]} packageContents
 */

/**
 * @param {CanonicalUniverse} universe
 * @returns {string[]}
 */
function validateCanonicalUniverse(universe) {
  const errors = [];
  if (!universe || typeof universe !== 'object') {
    return ['canonical universe is missing'];
  }
  const families = Array.isArray(universe.hardwareFamilies) ? universe.hardwareFamilies : [];
  const products = Array.isArray(universe.products) ? universe.products : [];
  const packageContents = Array.isArray(universe.packageContents) ? universe.packageContents : [];

  const familySlugs = new Set();
  for (const family of families) {
    if (!family?.slug) errors.push('hardware family missing slug');
    else if (familySlugs.has(family.slug)) errors.push(`duplicate hardware family slug: ${family.slug}`);
    else familySlugs.add(family.slug);
    if (!family?.name) errors.push(`hardware family ${family?.slug} missing name`);
  }

  const productBySlug = new Map();
  for (const product of products) {
    if (!product?.slug) {
      errors.push('product missing slug');
      continue;
    }
    if (productBySlug.has(product.slug)) {
      errors.push(`duplicate product slug: ${product.slug}`);
    }
    productBySlug.set(product.slug, product);

    if (!ENTITY_KINDS.includes(product.entityKind)) {
      errors.push(`${product.slug}: invalid entityKind ${product.entityKind}`);
    }
    if (!product.name) errors.push(`${product.slug}: missing name`);
    if (!RECOMMENDATION_STATUSES.includes(product.recommendationStatus)) {
      errors.push(`${product.slug}: invalid recommendationStatus`);
    }
    if (!LIFECYCLE_STATUSES.includes(product.lifecycleStatus)) {
      errors.push(`${product.slug}: invalid lifecycleStatus`);
    }
    if (product.hardwareFamilySlug && !familySlugs.has(product.hardwareFamilySlug)) {
      errors.push(`${product.slug}: unknown hardwareFamilySlug ${product.hardwareFamilySlug}`);
    }
    for (const role of product.roles || []) {
      if (!SYSTEM_ROLES.includes(role)) {
        errors.push(`${product.slug}: invalid system role ${role}`);
      }
    }
    if (product.entityKind === 'package' && (product.roles || []).length > 0) {
      errors.push(`${product.slug}: packages should not have system roles; assign roles to components`);
    }
  }

  const aliasToSlug = new Map();
  for (const product of products) {
    for (const alias of product.aliases || []) {
      if (productBySlug.has(alias)) {
        errors.push(`${product.slug}: alias ${alias} collides with a canonical slug`);
      }
      if (aliasToSlug.has(alias)) {
        errors.push(`duplicate alias ${alias}`);
      }
      aliasToSlug.set(alias, product.slug);
    }
  }

  const contentKeys = new Set();
  for (const row of packageContents) {
    const key = `${row.packageSlug}::${row.componentSlug}`;
    if (contentKeys.has(key)) errors.push(`duplicate package content ${key}`);
    contentKeys.add(key);

    const parent = productBySlug.get(row.packageSlug);
    const child = productBySlug.get(row.componentSlug);
    if (!parent) errors.push(`package content parent missing: ${row.packageSlug}`);
    else if (parent.entityKind !== 'package') {
      errors.push(`${row.packageSlug} has package contents but entityKind is not package`);
    }
    if (!child) errors.push(`package content component missing: ${row.componentSlug}`);
    if (row.packageSlug === row.componentSlug) {
      errors.push(`${row.packageSlug} cannot contain itself`);
    }
    if (row.quantity != null && Number(row.quantity) <= 0) {
      errors.push(`${key}: quantity must be positive`);
    }
  }

  for (const product of products) {
    if (product.entityKind !== 'package') continue;
    const hasContents = packageContents.some((row) => row.packageSlug === product.slug);
    if (!hasContents) errors.push(`${product.slug}: package has no contents`);
  }

  const lvs44 = products.filter((p) =>
    p.slug === 'livescope-2-lvs44' || p.modelNumber === 'LVS44'
  );
  if (lvs44.length !== 1) {
    errors.push(`expected exactly one canonical LVS44, found ${lvs44.length}`);
  }

  const gls10 = productBySlug.get('garmin-gls-10');
  if (!gls10) errors.push('garmin-gls-10 is required');
  else if (gls10.entityKind !== 'product') errors.push('garmin-gls-10 must be a product, not a package');
  else {
    const roles = gls10.roles || [];
    if (!roles.includes('SONAR_PROCESSOR_INTERFACE')) {
      errors.push('garmin-gls-10 must satisfy SONAR_PROCESSOR_INTERFACE');
    }
    if (roles.includes('LIVE_SONAR')) {
      errors.push('garmin-gls-10 must not satisfy LIVE_SONAR');
    }
  }

  const liveScope2Slugs = [
    'livescope-2-lvs44',
    'livescope-2-lvs42hd',
    'livescope-2-lvs42hd-ice',
  ];
  for (const slug of liveScope2Slugs) {
    const product = productBySlug.get(slug);
    if (!product) {
      errors.push(`${slug} is required`);
      continue;
    }
    const roles = product.roles || [];
    if (!roles.includes('LIVE_SONAR') || !roles.includes('SONAR_PROCESSOR_INTERFACE')) {
      errors.push(`${slug} must satisfy LIVE_SONAR and SONAR_PROCESSOR_INTERFACE`);
    }
  }

  const glsRequiredSonarSlugs = [
    'garmin-lvs32',
    'garmin-lvs34',
    'garmin-lvs34-if',
    'garmin-lvs62',
  ];
  for (const slug of glsRequiredSonarSlugs) {
    const product = productBySlug.get(slug);
    if (!product) {
      errors.push(`${slug} is required`);
      continue;
    }
    const roles = product.roles || [];
    if (!roles.includes('LIVE_SONAR')) {
      errors.push(`${slug} must satisfy LIVE_SONAR`);
    }
    if (roles.includes('SONAR_PROCESSOR_INTERFACE')) {
      errors.push(`${slug} must not independently satisfy SONAR_PROCESSOR_INTERFACE; GLS 10 provides that role`);
    }
  }

  const approvedBatterySlugs = [
    '8ah-lifepo4-battery-only',
    '19ah-nmc-+-charger',
    '32ah-nmc-+-charger-+-bt',
    '52ah-nmc-+-charger-+-bt',
  ];
  for (const slug of approvedBatterySlugs) {
    const product = productBySlug.get(slug);
    if (!product) {
      errors.push(`${slug} is required`);
      continue;
    }
    const roles = product.roles || [];
    if (roles.length !== 1 || roles[0] !== 'POWER_SOURCE') {
      errors.push(`${slug} must satisfy POWER_SOURCE only; batteries are excluded from the compatibility graph`);
    }
  }
  if (universe.compatibilityRelationships || universe.compatibilityRules) {
    errors.push('compatibility relationships must not be stored in the Product Source of Truth catalog');
  }

  const lvs34 = productBySlug.get('garmin-lvs34');
  if (!lvs34) errors.push('garmin-lvs34 transducer component is required');
  else if (lvs34.entityKind !== 'product') errors.push('garmin-lvs34 must be a product');

  const plusSystem = productBySlug.get('livescope-plus-lvs34');
  if (!plusSystem) errors.push('livescope-plus-lvs34 (LiveScope Plus System) is required');
  else if (plusSystem.entityKind !== 'package') {
    errors.push('livescope-plus-lvs34 must be a package (retailer listings are the system, not the transducer alone)');
  }

  const plusContents = packageContents.filter((row) => row.packageSlug === 'livescope-plus-lvs34');
  const plusComponentSlugs = plusContents.map((row) => row.componentSlug).sort();
  if (!plusComponentSlugs.includes('garmin-lvs34') || !plusComponentSlugs.includes('garmin-gls-10')) {
    errors.push('LiveScope Plus System must contain garmin-lvs34 and garmin-gls-10');
  }

  const rec9210 = productBySlug.get('gpsmap-9210xsv');
  const rec9213 = productBySlug.get('gpsmap-9213xsv');
  if (!rec9210 || rec9210.recommendationStatus !== 'RECOMMENDED') {
    errors.push('gpsmap-9210xsv must exist as RECOMMENDED');
  }
  if (!rec9213 || rec9213.recommendationStatus !== 'RECOMMENDED') {
    errors.push('gpsmap-9213xsv must exist as RECOMMENDED');
  }

  for (const banned of ['gpsmap-9010xsv', 'gpsmap-9013xsv', '9010xsv', '9013xsv']) {
    if (productBySlug.has(banned)) {
      errors.push(`${banned} must not be in the recommendation catalog`);
    }
  }

  for (const product of products) {
    const haystack = `${product.slug} ${product.modelNumber || ''} ${product.name}`.toLowerCase();
    if (
      product.recommendationStatus === 'RECOMMENDED' &&
      (haystack.includes('9010xsv') || haystack.includes('9013xsv'))
    ) {
      errors.push(`${product.slug}: worldwide 9010/9013 must not be recommended`);
    }
    if (product.recommendationStatus === 'RECOMMENDED' && /summit/i.test(`${product.slug} ${product.name}`)) {
      errors.push(`${product.slug}: Summit products must not be RECOMMENDED`);
    }
    if (product.recommendationStatus === 'RECOMMENDED' && /vexilar/i.test(`${product.slug} ${product.name}`)) {
      errors.push(`${product.slug}: Vexilar products must not be RECOMMENDED`);
    }
    if (product.recommendationStatus === 'RECOMMENDED' && /beatdown/i.test(`${product.slug} ${product.name}`)) {
      errors.push(`${product.slug}: BeatDown poles must not be RECOMMENDED`);
    }
  }

  const fishArmor = products.filter((p) => /fish.?armor/i.test(`${p.slug} ${p.name}`));
  if (fishArmor.length > 0) {
    errors.push('Fish Armor products are not verified for this seed and must remain unseeded');
  }

  return errors;
}

/**
 * @param {string} slug
 * @param {CanonicalUniverse} universe
 * @returns {CanonicalProductRecord|null}
 */
function getCanonicalProduct(slug, universe) {
  if (!slug || !universe) return null;
  return (universe.products || []).find((product) => product.slug === slug) || null;
}

/**
 * Resolves an exact canonical slug or product alias to one product.
 * Family-level phrases that are not stored as aliases return null.
 * @param {string} alias
 * @param {CanonicalUniverse} universe
 * @returns {CanonicalProductRecord|null}
 */
function resolveCanonicalAlias(alias, universe) {
  if (!alias || !universe) return null;
  const exact = getCanonicalProduct(alias, universe);
  if (exact) return exact;
  return (universe.products || []).find((product) => (product.aliases || []).includes(alias)) || null;
}

/**
 * @param {string} slug
 * @param {CanonicalUniverse} universe
 * @returns {HardwareFamilyRecord|null}
 */
function getHardwareFamily(slug, universe) {
  if (!slug || !universe) return null;
  return (universe.hardwareFamilies || []).find((family) => family.slug === slug) || null;
}

/**
 * @param {string} packageSlug
 * @param {CanonicalUniverse} universe
 * @returns {PackageContentRecord[]}
 */
function getPackageContents(packageSlug, universe) {
  if (!packageSlug || !universe) return [];
  return (universe.packageContents || []).filter((row) => row.packageSlug === packageSlug);
}

module.exports = {
  ENTITY_KINDS,
  RECOMMENDATION_STATUSES,
  LIFECYCLE_STATUSES,
  SYSTEM_ROLES,
  OFFER_CONDITIONS,
  validateCanonicalUniverse,
  getCanonicalProduct,
  resolveCanonicalAlias,
  getHardwareFamily,
  getPackageContents,
};
